import { AiCode } from './AiCode';

interface IApplicationIdentifier {
    readonly code: string;
    readonly length: number;
    readonly fractional?: boolean;
}

/* Application Identifiers (AIs) are numeric prefixes in the GS1-128 barcode
 * These codes identify the meaning and format of data attributes and are defined by GS1 global standards
 *
 * This data is a mapping of AI code to data value length and if the code is fractional
 * Fractional codes have an additional digit between the code and data value that defines a numerical decimal position
 * Codes derived from https://www.gs1.org/standards/barcodes-epcrfid-id-keys/gs1-general-specifications
 */
const data: IApplicationIdentifier[] = [
    {
        code: AiCode.SerialShippingContainerCode,
        length: 18,
    },
    {
        code: AiCode.GTIN,
        length: 14,
    },
    {
        code: AiCode.GTINContainer,
        length: 14,
    },
    {
        code: AiCode.MTOGTIN,
        length: 14,
    },
    {
        code: AiCode.BatchLotNumber,
        length: 20,
    },
    {
        code: AiCode.ProductionDate,
        length: 6,
    },
    {
        code: AiCode.InvoiceDueDate,
        length: 6,
    },
    {
        code: AiCode.PackagingDate,
        length: 6,
    },
    {
        code: AiCode.BestBeforeDate,
        length: 6,
    },
    {
        code: AiCode.SellByDate,
        length: 6,
    },
    {
        code: AiCode.ExpirationDate,
        length: 6,
    },
    {
        code: AiCode.InternalProductVariant,
        length: 2,
    },
    {
        code: AiCode.SerialNumber,
        length: 20,
    },
    {
        code: AiCode.ConsumerProductVariant,
        length: 20,
    },
    {
        code: AiCode.ThirdPartyGTIN,
        length: 28,
    },
    {
        code: AiCode.AdditionalProductIdentification,
        length: 30,
    },
    {
        code: AiCode.CustomerPartNumber,
        length: 30,
    },
    {
        code: AiCode.MadeToOrderVariationNumber,
        length: 6,
    },
    {
        code: AiCode.PackagingComponentNumber,
        length: 20,
    },
    {
        code: AiCode.SecondarySerialNumber,
        length: 30,
    },
    {
        code: AiCode.ReferencetoSourceEntity,
        length: 30,
    },
    {
        code: AiCode.GlobalDocumentTypeIdentifier,
        length: 17,
    },
    {
        code: AiCode.GLNExtensionComponent,
        length: 20,
    },
    {
        code: AiCode.GlobalCouponNumberGCN,
        length: 25,
    },
    {
        code: AiCode.CountOfItems,
        length: 8,
    },
    {
        code: AiCode.ProductNetWeightKg,
        fractional: true,
        length: 6,
    },
    {
        code: AiCode.ProductLength1stDimensionM,
        fractional: true,
        length: 6,
    },
    {
        code: AiCode.ProductWidthDiameter2ndDimensionM,
        fractional: true,
        length: 6,
    },
    {
        code: AiCode.ProductDepthThicknessHeight3rdDimensionM,
        fractional: true,
        length: 6,
    },
    {
        code: AiCode.ProductAreaSqM,
        fractional: true,
        length: 6,
    },
    {
        code: AiCode.ProductNetVolumeL,
        fractional: true,
        length: 6,
    },
    {
        code: AiCode.ProductNetVolumeM,
        fractional: true,
        length: 6,
    },
    {
        code: AiCode.ProductNetWeightLbs,
        fractional: true,
        length: 6,
    },
    {
        code: AiCode.ProductLength1stDimensionIn,
        fractional: true,
        length: 6,
    },
    {
        code: AiCode.ProductLength1stDimensionFt,
        fractional: true,
        length: 6,
    },
    {
        code: AiCode.ProductLength1stDimensionYd,
        fractional: true,
        length: 6,
    },
    {
        code: AiCode.ProductWidthDiameter2ndDimensionIn,
        fractional: true,
        length: 6,
    },
    {
        code: AiCode.ProductWidthDiameter2ndDimensionFt,
        fractional: true,
        length: 6,
    },
    {
        code: AiCode.ProductWidthDiameter2ndDimensionYd,
        fractional: true,
        length: 6,
    },
    {
        code: AiCode.ProductDepthThicknessHeight3rdDimensionIn,
        fractional: true,
        length: 6,
    },
    {
        code: AiCode.ProductDepthThicknessHeight3rdDimensionFt,
        fractional: true,
        length: 6,
    },
    {
        code: AiCode.ProductDepthThickness3rdDimensionYd,
        fractional: true,
        length: 6,
    },
    {
        code: AiCode.ContainerGrossWeightKg,
        fractional: true,
        length: 6,
    },
    {
        code: AiCode.ContainerLength1stDimensionM,
        fractional: true,
        length: 6,
    },
    {
        code: AiCode.ContainerWidthDiameter2ndDimensionM,
        fractional: true,
        length: 6,
    },
    {
        code: AiCode.ContainerDepthThickness3rdDimensionM,
        fractional: true,
        length: 6,
    },
    {
        code: AiCode.ContainerAreaSqM,
        fractional: true,
        length: 6,
    },
    {
        code: AiCode.ContainerGrossVolumeL,
        fractional: true,
        length: 6,
    },
    {
        code: AiCode.ContainerGrossVolumeCubicM,
        fractional: true,
        length: 6,
    },
    {
        code: AiCode.ContainerGrossKgPerSqM,
        fractional: true,
        length: 6,
    },
    {
        code: AiCode.ContainerGrossWeightLbs,
        fractional: true,
        length: 6,
    },
    {
        code: AiCode.ContainerLength1stDimensionIn,
        fractional: true,
        length: 6,
    },
    {
        code: AiCode.ContainerLength1stDimensionFt,
        fractional: true,
        length: 6,
    },
    {
        code: AiCode.ContainerLength1stDimensionYd,
        fractional: true,
        length: 6,
    },
    {
        code: AiCode.ContainerWidthDiameter2ndDimensionIn,
        fractional: true,
        length: 6,
    },
    {
        code: AiCode.ContainerWidthDiameter2ndDimensionFt,
        fractional: true,
        length: 6,
    },
    {
        code: AiCode.ContainerWidthDiameter2ndDimensionYd,
        fractional: true,
        length: 6,
    },
    {
        code: AiCode.ContainerDepthThicknessHeight3rdDimensionIn,
        fractional: true,
        length: 6,
    },
    {
        code: AiCode.ContainerDepthThicknessHeight3rdDimensionFt,
        fractional: true,
        length: 6,
    },
    {
        code: AiCode.ContainerDepthThicknessHeight3rdDimensionYd,
        fractional: true,
        length: 6,
    },
    {
        code: AiCode.ProductAreaSqIn,
        fractional: true,
        length: 6,
    },
    {
        code: AiCode.ProductAreaSqFt,
        fractional: true,
        length: 6,
    },
    {
        code: AiCode.ProductAreaSqYd,
        fractional: true,
        length: 6,
    },
    {
        code: AiCode.ContainerAreaSqIn,
        fractional: true,
        length: 6,
    },
    {
        code: AiCode.ContainerAreaSqFt,
        fractional: true,
        length: 6,
    },
    {
        code: AiCode.ContainerAreaSqYd,
        fractional: true,
        length: 6,
    },
    {
        code: AiCode.NetWeightTroyOz,
        fractional: true,
        length: 6,
    },
    {
        code: AiCode.NetWeightVolumeOz,
        fractional: true,
        length: 6,
    },
    {
        code: AiCode.ProductVolumeQt,
        fractional: true,
        length: 6,
    },
    {
        code: AiCode.ProductVolumeGal,
        fractional: true,
        length: 6,
    },
    {
        code: AiCode.ContainerGrossVolumeQt,
        fractional: true,
        length: 6,
    },
    {
        code: AiCode.ContainerGrossVolumeUSGal,
        fractional: true,
        length: 6,
    },
    {
        code: AiCode.ProductVolumeCubicIn,
        fractional: true,
        length: 6,
    },
    {
        code: AiCode.ProductVolumeCubicFt,
        fractional: true,
        length: 6,
    },
    {
        code: AiCode.ProductVolumeCubicYd,
        fractional: true,
        length: 6,
    },
    {
        code: AiCode.ContainerGrossVolumeCubicIn,
        fractional: true,
        length: 6,
    },
    {
        code: AiCode.ContainerGrossVolumeCubicFt,
        fractional: true,
        length: 6,
    },
    {
        code: AiCode.ContainerGrossVolumeCubicYd,
        fractional: true,
        length: 6,
    },
    {
        code: AiCode.NumberofUnitsContained,
        length: 8,
    },
    {
        code: AiCode.AmountPayableLocalCurrency,
        fractional: true,
        length: 15,
    },
    {
        code: AiCode.AmountPayableISOCurrencyCode,
        fractional: true,
        length: 18,
    },
    {
        code: AiCode.AmountPayableSingleItemLocalCurrency,
        fractional: true,
        length: 15,
    },
    {
        code: AiCode.AmountPayableSingleItemxWithISOcurrencycode,
        fractional: true,
        length: 18,
    },
    {
        code: AiCode.PercentDiscountOfCoupon,
        fractional: true,
        length: 18,
    },
    {
        code: AiCode.AmountPayablePerUnitOfMeasure,
        fractional: true,
        length: 6,
    },
    {
        code: AiCode.CustomerPurchaseOrderNumber,
        length: 30,
    },
    {
        code: AiCode.ConsignmentNumber,
        length: 30,
    },
    {
        code: AiCode.GlobalShipmentId,
        length: 17,
    },
    {
        code: AiCode.RoutingCode,
        length: 30,
    },
    {
        code: AiCode.ShipToDeliverToLocationCodeGlobalLocationNumber,
        length: 13,
    },
    {
        code: AiCode.BillToInvoiceLocationCodeGlobalLocationNumber,
        length: 13,
    },
    {
        code: AiCode.PurchaseFromLocationCodeGlobalLocationNumber,
        length: 13,
    },
    {
        code: AiCode.ShipForDeliverForOrForwardToLocationCode,
        length: 13,
    },
    {
        code: AiCode.IdentificationOfAPhysicAllocation,
        length: 13,
    },
    {
        code: AiCode.InvoicingPartyGlobalLocationNumber,
        length: 13,
    },
    {
        code: AiCode.ProductionLocationGlobalLocationNumber,
        length: 13,
    },
    {
        code: AiCode.PartyGlobalLocationNumber,
        length: 13,
    },
    {
        code: AiCode.ShipToDeliverToPostalCodeSinglePostalAuthority,
        length: 20,
    },
    {
        code: AiCode.ShipToDeliverToPostalCodeISOCountryCode,
        length: 15,
    },
    {
        code: AiCode.CountryOfOrigin,
        length: 3,
    },
    {
        code: AiCode.CountryOrCountriesOfInitialProcessing,
        length: 15,
    },
    {
        code: AiCode.CountryOfProcessing,
        length: 3,
    },
    {
        code: AiCode.CountryOfDisassembly,
        length: 3,
    },
    {
        code: AiCode.CountryOfFullProcessChain,
        length: 3,
    },
    {
        code: AiCode.CountrySubdivisionOriginCode,
        length: 3,
    },
    {
        code: AiCode.ShipToCompanyName,
        length: 35,
    },
    {
        code: AiCode.ShipToContactName,
        length: 35,
    },
    {
        code: AiCode.ShipToAddressLine1,
        length: 70,
    },
    {
        code: AiCode.ShipToAddressLine2,
        length: 70,
    },
    {
        code: AiCode.ShipToSuburb,
        length: 70,
    },
    {
        code: AiCode.ShipToLocality,
        length: 70,
    },
    {
        code: AiCode.ShipToRegion,
        length: 70,
    },
    {
        code: AiCode.ShipToCountryCode,
        length: 2,
    },
    {
        code: AiCode.ShipToTelephoneNumber,
        length: 30,
    },
    {
        code: AiCode.ShipToGeo,
        length: 20,
    },
    {
        code: AiCode.ReturnToCompanyName,
        length: 35,
    },
    {
        code: AiCode.ReturnToContactName,
        length: 35,
    },
    {
        code: AiCode.ReturnToAddressLine1,
        length: 70,
    },
    {
        code: AiCode.ReturnToAddressLine2,
        length: 70,
    },
    {
        code: AiCode.ReturnToSuburb,
        length: 70,
    },
    {
        code: AiCode.ReturnToLocality,
        length: 70,
    },
    {
        code: AiCode.ReturnToRegion,
        length: 70,
    },
    {
        code: AiCode.ReturnToCountryCode,
        length: 2,
    },
    {
        code: AiCode.ReturnToPostalCode,
        length: 20,
    },
    {
        code: AiCode.ReturnToTelephoneNumber,
        length: 30,
    },
    {
        code: AiCode.ServiceCodeDescription,
        length: 35,
    },
    {
        code: AiCode.DangerousGoodsFlag,
        length: 1,
    },
    {
        code: AiCode.AuthorityToLeaveFlag,
        length: 1,
    },
    {
        code: AiCode.SignatureRequiredFlag,
        length: 1,
    },
    {
        code: AiCode.NotBeforeDeliveryDate,
        length: 10,
    },
    {
        code: AiCode.NotAfterDeliveryDate,
        length: 10,
    },
    {
        code: AiCode.ReleaseDate,
        length: 6,
    },
    {
        code: AiCode.MaxTempF,
        length: 6,
    },
    {
        code: AiCode.MaxTempC,
        length: 6,
    },
    {
        code: AiCode.MinTempF,
        length: 6,
    },
    {
        code: AiCode.MinTempC,
        length: 6,
    },
    {
        code: AiCode.NATOStockNumberNSN,
        length: 13,
    },
    {
        code: AiCode.UNECEMeatCarcassesAndCutsClassification,
        length: 30,
    },
    {
        code: AiCode.ExpirationDateAndTime,
        length: 10,
    },
    {
        code: AiCode.ActivePotency,
        length: 4,
    },
    {
        code: AiCode.CatchArea,
        length: 12,
    },
    {
        code: AiCode.FirstFreezeDate,
        length: 6,
    },
    {
        code: AiCode.HarvestDate,
        length: 12,
    },

    {
        code: AiCode.AquaticSpecies,
        length: 3,
    },
    {
        code: AiCode.FishingGearType,
        length: 10,
    },
    {
        code: AiCode.ProductionMethod,
        length: 2,
    },
    {
        code: AiCode.TestByDate,
        length: 10,
    },
    {
        code: AiCode.RefurbishmentLot,
        length: 20,
    },
    {
        code: AiCode.FunctionalStatus,
        length: 20,
    },
    {
        code: AiCode.RevisionStatus,
        length: 20,
    },
    {
        code: AiCode.GlobalIndividualAssetIdentifierOfAssembly,
        length: 30,
    },
    {
        code: AiCode.ProcessorApproval,
        length: 31,
    },
    {
        code: AiCode.UICExtension,
        length: 4,
    },
    {
        code: AiCode.UFRGTUnitType,
        length: 4,
    },
    {
        code: AiCode.NationalHealthcareReimburseNumGermanyPzn,
        length: 20,
    },
    {
        code: AiCode.NationalHealthcareReimburseNumFranceCip,
        length: 20,
    },
    {
        code: AiCode.NationalHealthcareReimburseNumSpainCn,
        length: 20,
    },
    {
        code: AiCode.NationalHealthcareReimburseNumBrazilDrn,
        length: 20,
    },
    {
        code: AiCode.NationalHealthcareReimburseNumPortugalAim,
        length: 20,
    },
    {
        code: AiCode.NationalHealthcareReimburseNumUsaNdc,
        length: 20,
    },
    {
        code: AiCode.NationalHealthcareReimburseNumItalyAFIA,
        length: 20,
    },
    {
        code: AiCode.CertificationReferenceNum,
        length: 30,
    },
    {
        code: AiCode.ProtocolId,
        length: 20,
    },
    {
        code: AiCode.AIDCMediaType,
        length: 2,
    },
    {
        code: AiCode.VersionControlNumber,
        length: 25,
    },
    {
        code: AiCode.DateOfBirth,
        length: 8,
    },
    {
        code: AiCode.DateAndTimeOfBirth,
        length: 12,
    },
    {
        code: AiCode.BiologicalSex,
        length: 1,
    },
    {
        code: AiCode.FamilyName,
        length: 40,
    },
    {
        code: AiCode.GivenName,
        length: 40,
    },
    {
        code: AiCode.Suffix,
        length: 10,
    },
    {
        code: AiCode.FullName,
        length: 90,
    },
    {
        code: AiCode.PersonAddr,
        length: 70,
    },
    {
        code: AiCode.BirthSequence,
        length: 3,
    },
    {
        code: AiCode.BabyOfFamilyName,
        length: 40,
    },
    {
        code: AiCode.RollProductsWidthLengthCoreDiameterDirectionSplices,
        length: 14,
    },
    {
        code: AiCode.MobilePhoneIdentifier,
        length: 20,
    },
    {
        code: AiCode.GlobalReturnableAssetIdentifier,
        length: 30,
    },
    {
        code: AiCode.GlobalIndividualAssetIdentifier,
        length: 30,
    },
    {
        code: AiCode.PricePerUnitOfMeasure,
        length: 6,
    },
    {
        code: AiCode.IdentificationOfTheComponentsOfAnItem,
        length: 18,
    },
    {
        code: AiCode.InternationalBankAccountNumber,
        length: 30,
    },
    {
        code: AiCode.DatetimeOfProduction,
        length: 12,
    },
    {
        code: AiCode.OpticalReadableSensorIndicator,
        length: 50,
    },
    {
        code: AiCode.ComponentPartIdentifier,
        length: 30,
    },
    {
        code: AiCode.ComponentPartSerialNumber,
        length: 12,
    },
    {
        code: AiCode.SoftwareVersion,
        length: 20,
    },
    {
        code: AiCode.GlobalModelNumber,
        length: 25,
    },
    {
        code: AiCode.HighlyIndividualisedDeviceRegistrationIdentifier,
        length: 25,
    },
    {
        code: AiCode.GlobalServiceRelationNumber,
        length: 18,
    },
    {
        code: AiCode.GlobalServiceRelationshipNumber,
        length: 18,
    },
    {
        code: AiCode.ServiceRelationInstanceNumber,
        length: 10,
    },
    {
        code: AiCode.PaymentSlipPreferenceNumber,
        length: 25,
    },
    {
        code: AiCode.IdentificationOfPiecesOfTradeItem,
        length: 18,
    },
    {
        code: AiCode.DigitalSignature,
        length: 90,
    },
    {
        code: AiCode.CouponcodeIDNorthAmerica,
        length: 30,
    },
    {
        code: AiCode.LoyaltyPointsOfCoupon,
        length: 4,
    },
    {
        code: AiCode.PositiveOfferCouponId,
        length: 70,
    },
    {
        code: AiCode.ExtendedPackagingURL,
        length: 70,
    },
    {
        code: AiCode.MutuallyAgreedBetweenTradingPartners,
        length: 30,
    },
    {
        code: AiCode.InternalCompanyCodes91,
        length: 30,
    },
    {
        code: AiCode.InternalCompanyCodes92,
        length: 30,
    },
    {
        code: AiCode.InternalCompanyCodes93,
        length: 30,
    },
    {
        code: AiCode.InternalCompanyCodes94,
        length: 30,
    },
    {
        code: AiCode.InternalCompanyCodes95,
        length: 30,
    },
    {
        code: AiCode.InternalCompanyCodes96,
        length: 30,
    },
    {
        code: AiCode.InternalCompanyCodes97,
        length: 30,
    },
    {
        code: AiCode.InternalCompanyCodes98,
        length: 30,
    },
    {
        code: AiCode.InternalCompanyCodes99,
        length: 30,
    },
];

/*
 * This class an object representation of the data defined above
 *
 * An Application Identifier is composed of a numerical code of 2-4 digits, a code value of varying length, and
 * an extra digit if the code value can represent a decimal.  The extra digit defines the decimal position
 */
export class ApplicationIdentifier {
    private readonly code: string;
    private readonly length: number;
    private readonly fractional: boolean;

    constructor(code: string, length: number, fractional: boolean = false) {
        this.code = code;
        this.length = length;
        this.fractional = fractional;
    }

    public getTotalLength(): number {
        return this.code.length + this.length + (this.fractional === true ? 1 : 0);
    }

    public getCode(): string {
        return this.code;
    }

    public getLength(): number {
        return this.length;
    }

    public isFractional(): boolean {
        return this.fractional;
    }
}

export const APPLICATION_IDENTIFIERS_MAP: Map<string, ApplicationIdentifier> = new Map(data.map((ai) => [ai.code, new ApplicationIdentifier(ai.code, ai.length, ai.fractional)]));
